<template>
  <div>
    <page-header
      :actions="headerActions"
      left-side-class="col-xl-4 pb-2"
      :page="headerPage"
      right-side-class="col-xl-8 align-items-end d-flex pb-4 justify-content-end"
    />
    <board class="my-3" :details="board">
      <template #data-item="{ item }">
        <div v-if="item.status">
          <custom-balloon
            :background-color="item.status.color"
            dot
            :text="item.status.name"
          />
        </div>
      </template>
    </board>

    <error
      v-if="canSendFile"
      class="mt-3"
      is-alert-mode
      message="Batch procesado; falta enviar el archivo por SFTP, ya que ocurrió un error durante el proceso de envío."
    />

    <rundown class="my-3" :value="value" />

    <filter-manager
      v-model="filter.model"
      class="my-3"
      :display-advanced="false"
      :filters="filters"
      @search="search()"
    />
    <custom-separator class="my-3" text="Servicios" />
    <custom-result-not-found
      v-if="results.length == 0"
      class="my-3"
      text="No hay información disponible."
    />
    <div v-else>
      <result-row
        v-for="item in results"
        :key="item.id"
        class="my-3"
        :value="item"
      />

      <pagination
        v-if="showPagination"
        :data="pagination"
        show-text
        @pagination-go-page="search"
      />
    </div>
  </div>
</template>

<script>
//mixins
import storeMixin from '@/shared/mixins/storeGenerator';
import alertMixin from '@/mixins/alert.mixin.js';
import { price } from '@/filters';

//components
import PageHeader from 'PageHeader';
import Board from 'Board';
import CustomBalloon from 'custom-balloon';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import FilterManager from 'FilterManager';
import Error from 'custom-error';

//local components
import Rundown from './components/Rundown.vue';
import ResultRow from './components/ResultRow.vue';

//services
import {
  getBatch,
  getStatuses,
  getBatchServices,
  changeBatchStatus,
  sendFile,
} from '@/modules/medicaid/medicaid-claim.api';
import appServices from '@/modules/app/api.js';

//constants
import MedicaidBatchStatus from '@/utils/constants/medicaidBatchStatus';

export default {
  name: 'MedicaidClaimBatch',
  components: {
    PageHeader,
    Board,
    CustomBalloon,
    CustomSeparator,
    CustomResultNotFound,
    Error,
    Pagination,
    FilterManager,
    Rundown,
    ResultRow,
  },
  mixins: [storeMixin, alertMixin],
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      value: {},
      query: null,
      filter: {
        model: {
          status: null,
          studentSieOrName: null,
        },
        options: {
          statuses: [],
        },
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 1,
        pageCount: 1,
        count: 1,
        pageSize: 10,
      },
      results: [],
    };
  },
  computed: {
    headerPage() {
      return {
        title: 'Reclamaciones',
        icon: 'file-circle-exclamation-icon',
        routes: ['Inicio', 'Medicaid', ' Reclamaciones'],
      };
    },
    headerActions() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () =>
            this.$router.push({ name: 'medicaid-claims', query: this.query }),
        },
        {
          text: 'Enviar Archivo',
          icon: 'fas fa-file-upload',
          variant: 'success',
          visible: this.canSendFile,
          click: () => this.sendFile(),
        },
        {
          text: 'Descargar Documento',
          icon: 'fas fa-file-download',
          variant: 'success',
          visible: this.canDownloadDocument,
          click: () => this.downloadDocument(),
        },
        {
          text: 'Enviar a Reclamación',
          icon: 'fas fa-exclamation-circle',
          variant: 'success',
          visible: this.canSend,
          click: () => this.send(),
        },
      ];
    },
    board() {
      return [
        [
          {
            label: 'Estatus:',
            icon: 'fas fa-file',
            cols: 'col-12 col-md-3',
            status: this.value.status,
          },
          {
            label: 'Número de Batch:',
            icon: 'fa fa-file-invoice',
            cols: 'col-12 col-md-3',
            data: this.value.number,
          },
          {
            label: 'Lugar de Servicio:',
            icon: 'fas fa-map-marker-alt',
            cols: 'col-12 col-md-3',
            data: this.value.location?.name,
          },
          {
            label: 'Fecha de Solicitud:',
            icon: 'fas fa-calendar-edit',
            cols: 'col-12 col-md-3',
            data: this.$formatDate(this.value.date),
          },
          {
            label: 'Cantidad de Reclamaciones:',
            icon: 'fas fa-file-alt',
            cols: 'col-12 col-md-3',
            data: this.value.count,
          },
          {
            label: 'Monto a Reclamar:',
            icon: 'fas fa-money-check-alt',
            cols: 'col-12 col-md-3',
            data: isNaN(this.value.totalAmount)
              ? ''
              : price(this.value.totalAmount),
          },
        ],
      ];
    },
    filters() {
      return [
        {
          name: 'status',
          component: 'CustomDropDown',
          value: this.filter.model.status,
          options: this.filter.options.statuses,
          placeholder: 'Estatus',
          class: 'col-12 col-md-6',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
        },
        {
          name: 'studentSieOrName',
          component: 'CustomInput',
          value: this.filter.model.studentSieOrName,
          placeholder: 'SIE o Nombre de Estudiante',
          class: 'col-12 col-md-6',
        },
      ];
    },
    canSend() {
      return (
        this.isReviewed &&
        this.userHasPermissions('provider.medicaid.claims.batch.send')
      );
    },
    canSendFile() {
      return (
        this.value.status?.id === MedicaidBatchStatus.Submitted &&
        this.value.fileSent === false
      );
    },
    canDownloadDocument() {
      return (
        this.value.status?.id === MedicaidBatchStatus.Submitted &&
        this.value.documentId
      );
    },
    isReviewed() {
      return this.value.status?.id === MedicaidBatchStatus.Reviewed;
    },
    showPagination() {
      return (
        this.results.length > 0 &&
        this.pagination.totalItemCount > this.pagination.pageSize
      );
    },
  },
  async mounted() {
    await Promise.all([this.getBatch(), this.getStatuses(), this.search()]);
    this.query = this.$route.query;
  },
  methods: {
    async getBatch() {
      if (!this.id) return;
      const { data } = await getBatch(this.id);
      this.value = data ?? {};
    },
    async getStatuses() {
      const { data } = await getStatuses();
      this.filter.options.statuses = data;
    },
    async search(page = 1) {
      this.pagination.currentPage = page;

      const params = {
        statusId: this.filter.model.status?.id,
        studentSieOrName: this.filter.model.studentSieOrName,
      };
      const { data: results = { data: [] } } = await getBatchServices(
        this.id,
        params
      ).catch(() => {
        this.showError('Ocurrió un error al obtener los resultados');
        return { data: { data: [] } };
      });
      this.results = results.data.map((x) => ({
        ...x,
        checked: false,
        opened: false,
      }));

      this.pagination = {
        ...this.pagination,
        totalItemCount: results.totalItemCount,
        pageCount: results.pageCount,
        currentPage: results.currentPage,
        count: results.count,
      };
    },
    async sendFile() {
      if (!this.canSendFile) return;

      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea enviar el archivo por SFTP?',
        'Sí',
        'No'
      );
      if (!confirmed) return;

      const message = {
        success: 'Archivo enviado exitosamente.',
        error:
          'No fue posible enviar el archivo. Intente nuevamente o contacte al administrador.',
      };

      try {
        const response = await sendFile(this.id);
        const result = response?.data ?? response;

        if (result === true) {
          await this.getBatch();
          this.ShowToast('Éxito', message.success, 'success');
        } else {
          this.ShowToast('Error', message.error, 'error');
        }
      } catch (error) {
        this.ShowToast('Atención', error?.message || message.error, 'error');
      }
    },
    async send() {
      if (!this.canSend) return;
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea enviar el batch con ${this.value.count} servicio(s) a reclamación?`,
        'Sí',
        'No'
      );
      if (!confirmed) return;

      try {
        await changeBatchStatus(this.id, {
          status: MedicaidBatchStatus.Submitted,
        });
        this.ShowToast(
          '',
          'El batch fue enviado a reclamación exitosamente.',
          'success'
        );
        await Promise.all([this.getBatch(), this.search()]);
      } catch (error) {
        this.ShowToast(
          '',
          'No fue posible aprobar la solicitud. Intente nuevamente o contacte al administrador.',
          'error'
        );
      }
    },
    downloadDocument() {
      return appServices.downloadDocumentByAttachmentId(this.value.documentId);
    },
  },
};
</script>
<style scoped>
::v-deep .orange-icon {
  font-size: 18pt;
}
</style>
